import {
  IconActivity,
  IconAlertTriangle,
  IconBriefcase,
  IconBuilding,
  IconCash,
  IconCircleCheck,
  IconClock,
  IconCreditCard,
  IconExternalLink,
  IconFileDescription,
  IconFileText,
  IconInbox,
  IconLoader2,
  IconPalette,
  IconPhoto,
  IconPlus,
  IconSettings,
  IconTrash,
  IconUpload,
  IconUserPlus,
  IconUsers,
} from '@tabler/icons-react';
import type { FC } from 'react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { type ClientStatsData, getClientStats } from '@/api/client-stats';
import { deleteClientLogo, getClientLogoUrl, uploadClientLogo } from '@/api/clients';
import { getSupplierAddConfig, getSupplierLabels } from '@/api/suppliers';
import { ClientNotificationSettings } from '@/components/client-notification-settings';
import { ClientPaymentUploadSettings } from '@/components/client-payment-upload-settings';
import { DetailSectionHeader } from '@/components/detail-page-primitives';
import { PartyFileFormatsPanel } from '@/components/file-formats/party-file-formats-panel';
import { PaymentFileUploadModal } from '@/components/payment-file-upload';
import { SupplierAddModal } from '@/components/supplier-add-modal';
import { SupplierFileUploadModal } from '@/components/supplier-file-upload-modal';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useKeycloakAuth } from '@/contexts/keycloak-auth-context';
import { isInternalAdminUser } from '@/lib/authz';
import { formatCalendarDate, formatDate, formatDateTime } from '@/lib/utils';
import { ActivitySummaryTable, StatsCard } from './modals/client-stats';

interface Props {
  clientId: string;
  clientName: string;
  clientCode?: string | undefined;
}

const formatCurrency = (cents: number): string => {
  const dollars = cents / 100;
  if (dollars >= 1000000) return `$${(dollars / 1000000).toFixed(1)}M`;
  if (dollars >= 1000) return `$${(dollars / 1000).toFixed(1)}K`;
  return `$${dollars.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
};

const formatPaymentMethod = (method: string): string => {
  const methodLabels: Record<string, string> = {
    ACH: 'ACH',
    CHECK: 'Check',
    VIRTUAL_CARD: 'Virtual Card',
    PHYSICAL_CARD: 'Physical Card',
    SECURE_CARD: 'Secure Card',
    WIRE: 'Wire',
    UNKNOWN: 'Unknown',
    ach: 'ACH',
    check: 'Check',
    virtual_card: 'Virtual Card',
    physical_card: 'Physical Card',
    secure_card: 'Secure Card',
    wire: 'Wire',
  };
  return methodLabels[method] || method;
};

const EmptyState: FC<{ message: string }> = ({ message }) => (
  <div className="flex flex-col items-center justify-center py-8 text-center">
    <IconInbox className="h-10 w-10 text-muted-foreground/50 mb-2" />
    <p className="text-sm text-muted-foreground">{message}</p>
  </div>
);

/**
 * Full client detail content (stats, activity, methods, beneficiaries, branding,
 * settings, file formats) plus the internal action upload/add modals. Rendered on
 * the client detail subpage. Visible only to internal users (route is role-gated).
 */
export const ClientDetailContent: FC<Props> = ({ clientId, clientName, clientCode }) => {
  const navigate = useNavigate();
  const { user } = useKeycloakAuth();
  // Per-client settings (notifications, upload types) are internal-admin-only on the API,
  // so only surface the Settings tab to internal admins — Int-Basic would just 403.
  const canManageClientSettings = isInternalAdminUser(user?.roles);
  const [stats, setStats] = useState<ClientStatsData | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [logoPreview, setLogoPreview] = useState<string | null>(null);
  const [logoFile, setLogoFile] = useState<File | null>(null);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const logoInputRef = useRef<HTMLInputElement>(null);
  const [hasExistingLogo, setHasExistingLogo] = useState(false);
  const [isSupplierUploadOpen, setIsSupplierUploadOpen] = useState(false);
  const [isPaymentUploadOpen, setIsPaymentUploadOpen] = useState(false);
  const [isSingleSupplierOpen, setIsSingleSupplierOpen] = useState(false);
  const [clientSupplierLabel, setClientSupplierLabel] = useState(getSupplierLabels('supplier'));
  const [activeTab, setActiveTab] = useState('activity');
  const [fileFormatAddOpen, setFileFormatAddOpen] = useState(false);

  useEffect(() => {
    if (!clientId) return;
    getSupplierAddConfig(clientId)
      .then((cfg) => setClientSupplierLabel(getSupplierLabels(cfg.displayName)))
      .catch(() => setClientSupplierLabel(getSupplierLabels('supplier')));
  }, [clientId]);

  useEffect(() => {
    if (!clientId) return;
    setActiveTab('activity');
    setIsLoading(true);
    setError(null);
    getClientStats(clientId)
      .then((data) => {
        setStats(data);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Failed to load client statistics');
      })
      .finally(() => {
        setIsLoading(false);
      });

    getClientLogoUrl(clientId)
      .then((data) => {
        if (data.logoUrl) {
          setLogoPreview(data.logoUrl);
          setHasExistingLogo(true);
        }
      })
      .catch(() => {
        // ignore - logo fetch is non-critical
      });
  }, [clientId]);

  const handleLogoSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) {
      toast.error('File size must be less than 5MB');
      return;
    }
    const validTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!validTypes.includes(file.type)) {
      toast.error('Only JPEG, PNG, GIF, and WebP images are allowed');
      return;
    }
    setLogoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setLogoPreview(ev.target?.result as string);
    reader.readAsDataURL(file);
  };

  const handleLogoUpload = async () => {
    if (!logoFile) return;
    setIsUploadingLogo(true);
    try {
      const result = await uploadClientLogo(clientId, logoFile);
      setLogoPreview(result.logoUrl);
      setLogoFile(null);
      setHasExistingLogo(true);
      toast.success('Logo uploaded successfully');
    } catch {
      toast.error('Failed to upload logo');
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleLogoDelete = async () => {
    setIsUploadingLogo(true);
    try {
      await deleteClientLogo(clientId);
      setLogoPreview(null);
      setLogoFile(null);
      setHasExistingLogo(false);
      if (logoInputRef.current) logoInputRef.current.value = '';
      toast.success('Logo deleted successfully');
    } catch {
      toast.error('Failed to delete logo');
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleViewSuppliers = () => {
    navigate(`/suppliers?clientId=${clientId}`);
  };

  const handleViewPayments = () => {
    navigate(`/payments?clientId=${clientId}`);
  };

  if (error) {
    return (
      <div className="bg-destructive/15 border border-destructive text-destructive px-4 py-3 rounded-sm text-sm">
        {error}
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="space-y-4">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {[1, 2, 3].map((i) => (
            <Skeleton key={i} className="h-28 w-full rounded-sm" />
          ))}
        </div>
        <Skeleton className="h-10 w-full rounded-sm" />
        <Skeleton className="h-48 w-full rounded-sm" />
      </div>
    );
  }

  if (!stats) return null;

  return (
    <div className="space-y-6">
      <Card className="border-amber-200 bg-amber-50/40 py-0 dark:border-amber-500/30 dark:bg-amber-500/10">
        <DetailSectionHeader icon={IconBriefcase} title={`Internal Actions for ${clientName}`} />
        <CardContent className="space-y-3 p-4">
          <p className="text-sm text-muted-foreground">
            All actions here are performed on behalf of this client and data is associated with this
            client account.
          </p>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Button type="button" onClick={() => setIsSupplierUploadOpen(true)}>
              <IconUpload className="h-4 w-4 mr-2" />
              Upload {clientSupplierLabel.singular} File
            </Button>
            <Button type="button" onClick={() => setIsPaymentUploadOpen(true)}>
              <IconFileText className="h-4 w-4 mr-2" />
              Upload Payment File
            </Button>
            <Button type="button" onClick={() => setIsSingleSupplierOpen(true)}>
              <IconUserPlus className="h-4 w-4 mr-2" />
              Create One-Off {clientSupplierLabel.singular}
            </Button>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <StatsCard
          icon={<IconCash className="h-4 w-4" />}
          title="Total Volume"
          value={formatCurrency(stats.totalVolumeAllTimeCents)}
          subtitle="All-time"
        />
        <StatsCard
          icon={<IconCash className="h-4 w-4" />}
          title="Payments"
          value={stats.totalPaymentsAllTime.toLocaleString()}
          subtitle="All-time"
        />
        <StatsCard
          icon={<IconFileText className="h-4 w-4" />}
          title="Payment Files"
          value={stats.totalPaymentFilesAllTime.toLocaleString()}
          subtitle="All-time"
        />
        <StatsCard
          icon={<IconUsers className="h-4 w-4" />}
          title="Beneficiaries"
          value={stats.totalSuppliers.toLocaleString()}
          subtitle="Active"
        />
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList
          className={`grid w-full ${canManageClientSettings ? 'grid-cols-6' : 'grid-cols-5'}`}
        >
          <TabsTrigger value="activity" className="flex items-center gap-2">
            <IconActivity className="h-4 w-4" />
            Activity
          </TabsTrigger>
          <TabsTrigger value="methods" className="flex items-center gap-2">
            <IconCreditCard className="h-4 w-4" />
            Methods
          </TabsTrigger>
          <TabsTrigger value="suppliers" className="flex items-center gap-2">
            <IconBuilding className="h-4 w-4" />
            Beneficiaries
          </TabsTrigger>
          <TabsTrigger value="branding" className="flex items-center gap-2">
            <IconPalette className="h-4 w-4" />
            Branding
          </TabsTrigger>
          {canManageClientSettings && (
            <TabsTrigger value="settings" className="flex items-center gap-2">
              <IconSettings className="h-4 w-4" />
              Settings
            </TabsTrigger>
          )}
          <TabsTrigger value="file-formats" className="flex items-center gap-2">
            <IconFileDescription className="h-4 w-4" />
            File Formats
          </TabsTrigger>
        </TabsList>

        <TabsContent value="activity" className="space-y-4 mt-4">
          <ActivitySummaryTable
            rows={[
              {
                period: 'This Year',
                amountCents: stats.paymentTotalYTDCents,
                payments: stats.paymentsProcessedYTD,
                files: stats.paymentFilesProcessedYTD,
              },
              {
                period: 'This Month',
                amountCents: stats.paymentTotalMTDCents,
                payments: stats.paymentsProcessedMTD,
                files: stats.paymentFilesProcessedMTD,
              },
              {
                period: 'Today',
                amountCents: stats.paymentTotalTodayCents,
                payments: stats.paymentsProcessedToday,
                files: stats.paymentFilesProcessedToday,
              },
            ]}
            formatCurrency={formatCurrency}
          />

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Recent Payment Files */}
            <Card className="py-0">
              <DetailSectionHeader icon={IconFileText} title="Recent Files" />
              <CardContent className="p-4">
                {stats.recentPaymentFiles.length > 0 ? (
                  <div className="space-y-2">
                    {stats.recentPaymentFiles.map((file) => (
                      <div
                        key={file.id}
                        className="flex items-center justify-between p-2 bg-muted/50 text-sm"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="font-medium truncate">{file.fileName}</p>
                          <p className="text-xs text-muted-foreground">
                            {formatDate(file.createdAt)} • {file.paymentCount} payments
                          </p>
                        </div>
                        <div className="text-right ml-2">
                          <Badge variant="outline" className="text-xs">
                            {file.status}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmptyState message="No payment files yet" />
                )}
              </CardContent>
            </Card>

            {/* Recent Payments */}
            <Card className="py-0">
              <DetailSectionHeader icon={IconCash} title="Recent Payments" />
              <CardContent className="p-4">
                {stats.recentPayments.length > 0 ? (
                  <div className="space-y-2">
                    {stats.recentPayments.map((payment) => (
                      <div
                        key={payment.id}
                        className="flex items-center justify-between p-2 bg-muted/50 text-sm"
                      >
                        <div className="min-w-0 flex-1">
                          <p className="font-medium truncate">{payment.payeeName}</p>
                          <p className="text-xs text-muted-foreground">
                            {payment.executionDate
                              ? formatCalendarDate(payment.executionDate)
                              : formatDate(payment.createdAt)}
                          </p>
                        </div>
                        <div className="text-right ml-2">
                          <p className="font-semibold text-primary">
                            {formatCurrency(payment.amountCents)}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <EmptyState message="No payments yet" />
                )}
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        <TabsContent value="methods" className="space-y-4 mt-4">
          <Card className="py-0">
            <DetailSectionHeader icon={IconCreditCard} title="Payment Method Breakdown" />
            <CardContent className="p-4">
              {stats.paymentMethodBreakdown.length > 0 ? (
                <div className="space-y-2">
                  {stats.paymentMethodBreakdown.map((method) => (
                    <div
                      key={method.method}
                      className="flex items-center justify-between p-3 bg-muted/50"
                    >
                      <div className="flex items-center gap-3">
                        <Badge variant="outline">{formatPaymentMethod(method.method)}</Badge>
                        <span className="text-sm text-muted-foreground">
                          {method.count.toLocaleString()} payments
                        </span>
                      </div>
                      <span className="font-semibold">
                        {formatCurrency(method.totalAmountCents)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState message="No payment activity this month" />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="suppliers" className="space-y-4 mt-4">
          <Card className="py-0">
            <DetailSectionHeader icon={IconCircleCheck} title="Supplier Status Summary" />
            <CardContent className="p-4 pt-3">
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                <div className="flex items-center gap-3 rounded-lg bg-green-500/10 p-3">
                  <IconCircleCheck className="h-5 w-5 text-green-600 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground truncate">Verified</p>
                    <p className="font-bold text-green-600 text-lg">
                      {stats.supplierStatusSummary.verified}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 rounded-lg bg-yellow-500/10 p-3">
                  <IconAlertTriangle className="h-5 w-5 text-yellow-600 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground truncate">Unverified</p>
                    <p className="font-bold text-yellow-600 text-lg">
                      {stats.supplierStatusSummary.unverified}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 rounded-lg bg-blue-500/10 p-3">
                  <IconClock className="h-5 w-5 text-blue-600 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground truncate">Pending</p>
                    <p className="font-bold text-blue-600 text-lg">
                      {stats.supplierStatusSummary.pendingValidation}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-3 rounded-lg bg-purple-500/10 p-3">
                  <IconUserPlus className="h-5 w-5 text-purple-600 flex-shrink-0" />
                  <div className="min-w-0">
                    <p className="text-xs text-muted-foreground truncate">Enrolling</p>
                    <p className="font-bold text-purple-600 text-lg">
                      {stats.supplierStatusSummary.pendingEnrollment}
                    </p>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="py-0">
            <DetailSectionHeader icon={IconUsers} title="Top Suppliers by Volume" />
            <CardContent className="p-4">
              {stats.topSuppliers.length > 0 ? (
                <div className="space-y-2">
                  {stats.topSuppliers.map((supplier, index) => (
                    <div
                      key={supplier.id}
                      className="flex items-center justify-between p-3 bg-muted/50"
                    >
                      <div className="flex items-center gap-3">
                        <span className="flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold">
                          {index + 1}
                        </span>
                        <div>
                          <span className="font-medium">{supplier.displayName}</span>
                          <span className="text-xs text-muted-foreground ml-2">
                            {supplier.paymentCount} payments
                          </span>
                        </div>
                      </div>
                      <span className="font-semibold">
                        {formatCurrency(supplier.totalAmountCents)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <EmptyState message="No beneficiary payments this month" />
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {canManageClientSettings && (
          <TabsContent value="settings" className="space-y-4 mt-4">
            <ClientPaymentUploadSettings clientId={clientId} clientName={clientName} />
            <ClientNotificationSettings clientId={clientId} clientName={clientName} />
          </TabsContent>
        )}

        <TabsContent value="file-formats" className="space-y-4 mt-4">
          <Card className="py-0">
            <DetailSectionHeader
              icon={IconFileDescription}
              title="File Formats"
              meta={
                <Button size="sm" className="gap-1.5" onClick={() => setFileFormatAddOpen(true)}>
                  <IconPlus size={14} />
                  Add File Format
                </Button>
              }
            />
            <CardContent className="p-4">
              <PartyFileFormatsPanel
                partyId={clientId}
                scope="client"
                partyName={clientName}
                partyCode={clientCode}
                hideHeaderAdd
                addOpen={fileFormatAddOpen}
                onAddOpenChange={setFileFormatAddOpen}
              />
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="branding" className="space-y-4 mt-4">
          <Card className="py-0">
            <DetailSectionHeader icon={IconPhoto} title="Company Logo" />
            <CardContent className="p-4 pt-3">
              <div className="flex items-start gap-2">
                <div className="flex-shrink-0 w-32 h-32 border border-gray-200 rounded-lg overflow-hidden bg-muted/50 flex items-center justify-center">
                  {logoPreview ? (
                    <img
                      src={logoPreview}
                      alt={`${clientName} logo`}
                      className="w-full h-full object-contain"
                    />
                  ) : (
                    <IconPhoto className="h-12 w-12 text-muted-foreground/30" />
                  )}
                </div>
                {canManageClientSettings && (
                  <div className="flex flex-col gap-3">
                    <input
                      ref={logoInputRef}
                      type="file"
                      accept="image/jpeg,image/png,image/gif,image/webp"
                      className="hidden"
                      onChange={handleLogoSelect}
                    />
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => logoInputRef.current?.click()}
                      disabled={isUploadingLogo}
                    >
                      <IconUpload className="h-4 w-4 mr-2" />
                      {logoPreview ? 'Replace Logo' : 'Upload Logo'}
                    </Button>
                    {logoFile && (
                      <Button
                        type="button"
                        size="sm"
                        onClick={handleLogoUpload}
                        disabled={isUploadingLogo}
                      >
                        {isUploadingLogo && <IconLoader2 className="h-4 w-4 mr-2 animate-spin" />}
                        Save Logo
                      </Button>
                    )}
                    {hasExistingLogo && !logoFile && (
                      <Button
                        type="button"
                        variant="destructive"
                        size="sm"
                        onClick={handleLogoDelete}
                        disabled={isUploadingLogo}
                      >
                        <IconTrash className="h-4 w-4 mr-2" />
                        Remove Logo
                      </Button>
                    )}
                    <p className="text-xs text-muted-foreground">
                      Accepted formats: JPEG, PNG, GIF, WebP. Max 5MB.
                    </p>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      <div className="flex flex-col sm:flex-row gap-3 pt-2 border-t">
        <Button variant="outline" className="flex-1" onClick={handleViewSuppliers}>
          <IconUsers className="h-4 w-4 mr-2" />
          View Beneficiaries
          <IconExternalLink className="h-3 w-3 ml-2 opacity-50" />
        </Button>
        <Button variant="outline" className="flex-1" onClick={handleViewPayments}>
          <IconCash className="h-4 w-4 mr-2" />
          View Payments
          <IconExternalLink className="h-3 w-3 ml-2 opacity-50" />
        </Button>
      </div>

      <p className="text-xs text-muted-foreground text-center">
        Data generated at {formatDateTime(stats.generatedAt)}
      </p>

      <SupplierFileUploadModal
        open={isSupplierUploadOpen}
        onClose={() => setIsSupplierUploadOpen(false)}
        partyId={clientId}
        actingOnBehalfClientName={clientName}
      />

      <PaymentFileUploadModal
        open={isPaymentUploadOpen}
        onClose={() => setIsPaymentUploadOpen(false)}
        partyId={clientId}
        actingOnBehalfClientName={clientName}
      />

      <SupplierAddModal
        open={isSingleSupplierOpen}
        onClose={() => setIsSingleSupplierOpen(false)}
        partyId={clientId}
        actingOnBehalfClient={{ id: clientId, name: clientName }}
      />
    </div>
  );
};
